import React, { useEffect, useState, useMemo } from 'react';
import { Chip, CircularProgress, ThemeProvider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { maxSymbolQTY, validationMessages } from '../../../../utils/constants';
import { addToSkills, theme } from '../helpers/helpers';
import ValidationErrorLabel from '../../ValidationErrorLabel/ValidationErrorLabel';
import api from '../../../../api/apiSettings';
import colors from '../../../../scss/colors';
import classes from '../scss/addEditFormModal.module.scss';
import { setNotification } from '../../../../actions/appNotifications';

function SkillsForm(props) {
  const { addEditForm } = props;

  if (!addEditForm || !addEditForm.values) return null;

  const [, setSkillSymbolQty] = useState(0);
  const [valuesToAdd, setValuesToAdd] = useState([]);
  const [skillsFieldTouched, setSkillsFieldTouched] = useState(false);

  const dispatch = useDispatch();

  const [suggestions, setSuggestions] = useState([{ label: '', value: 0 }]);

  const [open, setOpen] = React.useState(false);
  const loading = open && suggestions?.length === 0;

  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const addHandler = (data) => {
    if (!data || (Array.isArray(data) && data.length === 0)) return;

    const skill = Array.isArray(data) ? data[0].label || data[0] : data.label || data;
    const updated = addToSkills(undefined, skill, addEditForm.values.skills);
    addEditForm.setFieldValue('skills', updated);
    setOpen(false);
  };

  const requestSuggestions = async (value) => {
    let suggestionsResponse = [];

    if (value?.length >= 3) {
      const recievedSuggestions = await api.get(`v1/skills?keyword=${value || ''}`);

      if (recievedSuggestions?.data?.Data.length) {
        suggestionsResponse = recievedSuggestions;
      } else if (value?.length <= maxSymbolQTY.SKILL) {
        suggestionsResponse = {
          data: {
            Data: [{ Name: value, Id: 0 }]
          }
        };
      }
    } else {
      suggestionsResponse = [];
    }

    setSuggestions(
      suggestionsResponse?.data?.Data.map((suggestion) => ({ label: suggestion?.Name, value: suggestion?.Id }))
    );
  };

  useEffect(() => {
    let active = true;
    if (!loading) {
      return undefined;
    }
    if (active) {
      requestSuggestions();
    }
    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setSuggestions([]);
    }
  }, [open]);

  const skillsArray = Object.values(addEditForm.values.skills).filter(
    (item) => typeof item === 'object' && item.SkillName
  );

  return (
    <>
      <div className={`${classes.skillsList} mb-5`}>
        {skillsArray.map((item) => (
          <ThemeProvider key={item.Id} theme={theme}>
            <Chip
              className={classes.chip}
              data-testid={`deleteSkill-${item.Id}`}
              label={item.SkillName}
              deleteIcon={<CloseIcon fontSize="small" />}
              color="primary"
              variant="outlined"
              onDelete={() =>
                addEditForm.setFieldValue(
                  'skills',
                  skillsArray.filter((skill) => skill.Id !== item.Id)
                )
              }
              sx={{
                color: colors.LIGHT_BLUE,
                fontFamily: 'Airswift Radius',
                fontSize: '14px',
                backgroundColor: colors.WHITE,
                '& .MuiChip-deleteIcon': {
                  color: colors.LIGHT_BLUE
                }
              }}
            />
          </ThemeProvider>
        ))}
      </div>
      <div className="row">
        <div className="flex flex-col w-full">
          <Autocomplete
            multiple
            disableCloseOnSelect
            disablePortal
            id="combo-box-demo"
            value={valuesToAdd}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            freeSolo
            options={suggestions || []}
            loading={loading}
            getOptionLabel={(option) => (option.label ? option.label?.toString() : undefined)}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') {
                setSkillsFieldTouched(true);
                setSkillSymbolQty(value.length);
                requestSuggestions(value);
              }
              if (reason === 'clear') {
                setSkillSymbolQty(0);
              }
            }}
            renderOption={(params, option) => {
              const alreadyExists = skillsArray.some((opt) => opt.SkillName === option.label);

              return !alreadyExists ? (
                <li {...params} className="d-flex justify-content-between align-items-center ms-3 me-1">
                  {option.label}
                </li>
              ) : null;
            }}
            renderInput={(params) => (
              <div className={`flex !flex-col ${classes.autocompleteInput}`}>
                {}
                <div className="flex flex-row w-full">
                  <TextField
                    {...params}
                    label={t('profile.builder.addskills')}
                    variant="outlined"
                    data-testid="search-field"
                    onBlur={() => setSkillsFieldTouched(false)}
                    onFocus={() => setSkillsFieldTouched(true)}
                    InputProps={{
                      ...params.InputProps,
                      onChange: (e) => {
                        setSkillSymbolQty(e.target.value.length);
                      },
                      onKeyDown: (e) => {
                        if (
                          e.key === 'Enter' &&
                          params.inputProps.value &&
                          params.inputProps.value.length <= maxSymbolQTY.SKILL
                        ) {
                          const newValue = params.inputProps.value.trim();
                          const alreadyExists = skillsArray.some(
                            (skill) => skill.SkillName?.toLowerCase() === newValue.toLowerCase()
                          );

                          if (!alreadyExists) {
                            const updated = [...skillsArray, { SkillName: newValue }];
                            addHandler(updated); // Or use setFieldValue if you’re updating Formik directly
                          }

                          if (alreadyExists) {
                            dispatch(
                              setNotification({
                                type: 'error',
                                message: 'Skill already exists'
                              })
                            );
                          }
                          setValuesToAdd([]); // To reset Autocomplete’s value
                          setSkillSymbolQty(0);
                          e.preventDefault(); // Prevent default form submission or autocomplete behavior
                        }

                        e.stopPropagation();
                      },
                      endAdornment: (
                        <>
                          {loading ? (
                            <CircularProgress
                              data-test="loading-spinner"
                              role="progressbar"
                              color="inherit"
                              size={20}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                    sx={{ maxWidth: 600 }}
                    value={params.inputProps.value}
                  />
                </div>
                <div className="flex flex-col w-full">
                  {params.inputProps.value.length < 3 && skillsFieldTouched && (
                    <div className={classes.infoMessage}>
                      <ValidationErrorLabel
                        color={colors.DARKEST_GREY}
                        message={validationMessage.SKILLS_MIN_CHAR_SEARCH}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            onChange={(event, value) => {
              setValuesToAdd([]);
              setSkillSymbolQty(value.length);
              addHandler(value);
            }}
            variant="standard"
            sx={{
              display: 'inline-block',
              minWidth: 300
            }}
          />
          {/* <ValidationErrorLabel
            classes="mt-2"
            color={skillSymbolQty > maxSymbolQTY.SKILL ? colors.RED_ERROR : colors.BLACK}
            message={`${skillSymbolQty}/${maxSymbolQTY.SKILL}`}
          /> */}
        </div>
      </div>
    </>
  );
}

export default SkillsForm;
